/**
 * ConversationSidebar — Conversation history panel for Maestro chat
 * MAESTRO:P0D
 *
 * Desktop: fixed-width left panel (w-64)
 * Mobile: bottom sheet drawer (fixed bottom-0, slides up)
 *
 * Features:
 *  - "New Conversation" button at top
 *  - List of conversations: title, message count, relative time
 *  - Active conversation highlighted with orange left border
 *  - Archive (trash) icon per row, confirmed via inline toggle
 *  - Load more when scrolled to bottom (pagination)
 *  - 48px min touch targets on mobile
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { Plus, Trash2, MessageSquare, ChevronDown } from 'lucide-react';

/**
 * Returns a human-readable relative time string.
 */
function relativeTime(iso) {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date(iso));
  } catch {
    return '';
  }
}

/**
 * Single conversation row.
 */
function ConversationRow({ conv, isActive, onSelect, onArchive }) {
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleArchiveClick = useCallback((e) => {
    e.stopPropagation();
    if (confirmDelete) {
      onArchive(conv.id);
    } else {
      setConfirmDelete(true);
    }
  }, [confirmDelete, conv.id, onArchive]);

  // Auto-dismiss confirm state
  useEffect(() => {
    if (!confirmDelete) return;
    const t = setTimeout(() => setConfirmDelete(false), 3000);
    return () => clearTimeout(t);
  }, [confirmDelete]);

  return (
    <div
      role="listitem"
      className={`
        group relative flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-150
        min-h-[48px] focus-within:outline-none
        ${isActive
          ? 'bg-orange-500/10 border border-orange-500/40'
          : 'hover:bg-zinc-800/60 border border-transparent'
        }
      `}
      onClick={() => onSelect(conv.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(conv.id); }
      }}
      tabIndex={0}
      aria-current={isActive ? 'true' : undefined}
      aria-label={`Conversation: ${conv.title || 'Untitled'}`}
    >
      <MessageSquare
        size={14}
        aria-hidden="true"
        className={`flex-shrink-0 ${isActive ? 'text-orange-400' : 'text-zinc-600 group-hover:text-zinc-400'}`}
      />

      <div className="flex-1 min-w-0">
        <p className={`text-xs truncate font-mono ${isActive ? 'text-orange-200' : 'text-zinc-300 group-hover:text-zinc-100'}`}>
          {conv.title || 'New conversation'}
        </p>
        <p className="text-[10px] text-zinc-600 font-mono flex items-center gap-1.5 mt-0.5">
          {conv.message_count != null && (
            <span>{conv.message_count} msg{conv.message_count !== 1 ? 's' : ''}</span>
          )}
          {conv.updated_at && <span>{relativeTime(conv.updated_at)}</span>}
        </p>
      </div>

      {/* Archive button */}
      <button
        type="button"
        onClick={handleArchiveClick}
        aria-label={confirmDelete ? 'Confirm archive' : `Archive conversation: ${conv.title || 'Untitled'}`}
        className={`
          flex-shrink-0 p-1.5 rounded transition-all duration-150 opacity-0 group-hover:opacity-100
          focus:opacity-100 focus:outline-none focus:ring-1 focus:ring-orange-500
          min-h-[2rem] min-w-[2rem] flex items-center justify-center
          ${confirmDelete ? 'text-red-400 bg-red-500/10 opacity-100' : 'text-zinc-600 hover:text-red-400 hover:bg-red-500/10'}
        `}
      >
        <Trash2 size={12} aria-hidden="true" />
      </button>
    </div>
  );
}

/**
 * @param {Object} props
 * @param {Array} props.conversations - From useMaestroChat.conversationList
 * @param {string|null} props.activeId - Currently active conversation id
 * @param {(id: string) => void} props.onSelect - Load conversation by id
 * @param {() => void} props.onNew - Start new conversation
 * @param {(id: string) => void} props.onArchive - Archive conversation by id
 * @param {boolean} props.hasMore - Whether more conversations can be loaded
 * @param {boolean} props.listLoading - Loading state for the list
 * @param {() => void} props.onLoadMore - Load the next page
 * @param {boolean} [props.isMobileOpen] - Controls mobile bottom sheet visibility
 * @param {() => void} [props.onMobileClose] - Close mobile sheet
 */
export default function ConversationSidebar({
  conversations = [],
  activeId,
  onSelect,
  onNew,
  onArchive,
  hasMore,
  listLoading,
  onLoadMore,
  isMobileOpen = false,
  onMobileClose,
}) {
  const listRef = useRef(null);

  // Detect scroll-to-bottom on the list for infinite scroll
  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || !hasMore || listLoading) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
      onLoadMore();
    }
  }, [hasMore, listLoading, onLoadMore]);

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-3 py-3 border-b border-zinc-800 flex-shrink-0">
        <button
          type="button"
          onClick={onNew}
          aria-label="Start a new conversation"
          className="
            w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg
            bg-orange-500 hover:bg-orange-600 text-white text-xs font-mono font-semibold
            transition-all duration-150 active:scale-[0.98]
            focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-1 focus:ring-offset-zinc-950
            min-h-[48px]
          "
        >
          <Plus size={14} aria-hidden="true" />
          New Conversation
        </button>
      </div>

      {/* Conversation list */}
      <div
        ref={listRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5"
        role="list"
        aria-label="Conversation history"
      >
        {conversations.length === 0 && !listLoading && (
          <p className="text-center text-xs text-zinc-600 font-mono py-8 px-3">
            No conversations yet.{'\n'}Ask Maestro something to get started.
          </p>
        )}

        {conversations.map((conv) => (
          <ConversationRow
            key={conv.id}
            conv={conv}
            isActive={conv.id === activeId}
            onSelect={onSelect}
            onArchive={onArchive}
          />
        ))}

        {/* Load more button */}
        {hasMore && !listLoading && (
          <button
            type="button"
            onClick={onLoadMore}
            className="w-full flex items-center justify-center gap-1.5 py-2 text-[10px] text-zinc-600 hover:text-zinc-400 font-mono transition-colors focus:outline-none focus:ring-1 focus:ring-orange-500 rounded min-h-[40px]"
            aria-label="Load more conversations"
          >
            <ChevronDown size={12} aria-hidden="true" />
            Load more
          </button>
        )}

        {listLoading && (
          <div className="flex justify-center py-3" aria-live="polite" aria-label="Loading conversations">
            <span className="w-4 h-4 rounded-full border-2 border-zinc-700 border-t-orange-500 animate-spin" />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className="hidden md:flex flex-col w-64 flex-shrink-0 bg-zinc-950 border-r border-zinc-800 h-full"
        aria-label="Conversation history sidebar"
      >
        {sidebarContent}
      </aside>

      {/* Mobile bottom sheet */}
      {isMobileOpen && (
        <>
          {/* Backdrop */}
          <div
            className="md:hidden fixed inset-0 bg-black/60 z-40"
            onClick={onMobileClose}
            aria-hidden="true"
          />
          {/* Sheet */}
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Conversation history"
            className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-zinc-950 border-t border-zinc-800 rounded-t-2xl"
            style={{ maxHeight: '70vh' }}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-zinc-700" aria-hidden="true" />
            </div>
            <div style={{ height: 'calc(70vh - 2rem)', display: 'flex', flexDirection: 'column' }}>
              {sidebarContent}
            </div>
          </div>
        </>
      )}
    </>
  );
}
